import { useEffect, useState } from 'react';
import { View, FlatList, StyleSheet, ActivityIndicator, Text, ScrollView, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../types/navigation';
import { VideoPost } from '../types/video';
import VideoCard from '../components/video/VideoCard';
import { searchVideoPosts, getVideoPosts, getCustomFeed, getSavedFeeds } from '../services/bluesky';
import { convertPostToVideoPost } from '../utils/video';
import { getCache, setCache, getCacheKey, CACHE_DURATION } from '../utils/cache';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface FeedOption {
  id: string;
  label: string;
  uri?: string;
  type: 'system' | 'custom';
}

const SYSTEM_FEEDS: FeedOption[] = [
  { id: 'following', label: 'フォロー中', type: 'system' },
];

const VideoTopScreen = () => {
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const [videos, setVideos] = useState<VideoPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(true);
  const [feedOptions, setFeedOptions] = useState<FeedOption[]>(SYSTEM_FEEDS);
  const [selectedFeedId, setSelectedFeedId] = useState<string>('following');

  useEffect(() => {
    const initFeeds = async () => {
      try {
        const saved = await getSavedFeeds();
        // Followingを常に含める
        const options: FeedOption[] = [
          { id: 'following', label: 'フォロー中', type: 'system' }
        ];

        if (saved.pinned && saved.pinned.length > 0) {
          // ピン留めされたフィードを追加（重複を避ける）
          saved.pinned.forEach((f: any) => {
            if (f.uri !== 'following') {
              options.push({
                id: f.uri,
                label: f.displayName || '不明なフィード',
                uri: f.uri,
                type: 'custom'
              });
            }
          });
        }
        setFeedOptions(options);
        // デフォルトはFollowing（または最初のピン留め）
        setSelectedFeedId('following');
      } catch (error) {
        console.error('Failed to load saved feeds:', error);
      }
    };
    initFeeds();
  }, []);

  const fetchVideos = async (isRefresh = false, feedId: string = selectedFeedId) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        setCursor(undefined);
      } else {
        setLoading(true);
      }

      let result;
      const currentFeed = feedOptions.find(f => f.id === feedId);
      
      // キャッシュキーを生成
      const cacheKey = getCacheKey.videoFeed(feedId);
      
      // 初回読み込み時はキャッシュを確認
      if (isRefresh && !cursor) {
        const cachedData = await getCache<{ feed: any[], cursor?: string }>(cacheKey, CACHE_DURATION.VIDEO_FEED);
        if (cachedData) {
          const videoData = cachedData.feed
            .map(convertPostToVideoPost)
            .filter((v): v is VideoPost => v !== null);
          
          setVideos(videoData);
          setCursor(cachedData.cursor);
          setHasMore(!!cachedData.cursor && videoData.length > 0);
          setRefreshing(false);
          setLoading(false);
          return;
        }
      }
      
      if (currentFeed?.type === 'custom' && currentFeed.uri) {
        result = await getCustomFeed(currentFeed.uri, isRefresh ? undefined : cursor);
      } else {
        switch (feedId) {
          case 'following':
            result = await getVideoPosts(isRefresh ? undefined : cursor);
            break;
          case 'discover':
            result = await searchVideoPosts('video', isRefresh ? undefined : cursor);
            break;
          case 'all':
          default:
            result = await searchVideoPosts('', isRefresh ? undefined : cursor);
            break;
        }
      }

      const videoData = result.feed
        .map(convertPostToVideoPost)
        .filter((v): v is VideoPost => v !== null);

      if (isRefresh) {
        setVideos(videoData);
        // キャッシュに保存（初回読み込みのみ）
        if (!cursor) {
          await setCache(cacheKey, { feed: result.feed, cursor: result.cursor });
        }
      } else {
        setVideos(prev => [...prev, ...videoData]);
      }

      setCursor(result.cursor);
      setHasMore(!!result.cursor && videoData.length > 0);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (feedOptions.length > 0) {
      // フィードリストが読み込まれたとき、または選択が切り替わったときに取得
      fetchVideos(true, selectedFeedId);
    }
  }, [selectedFeedId, feedOptions]); // feedOptionsも監視対象に戻すが、fetchVideos内でのステート更新には注意

  const handleRefresh = () => {
    fetchVideos(true);
  };

  const handleLoadMore = () => {
    if (!loading && !refreshing && hasMore) {
      fetchVideos();
    }
  };

  const handleFeedSelect = (feedId: string) => {
    setSelectedFeedId(feedId);
    setVideos([]);
    setCursor(undefined);
  };

  const renderFeedSelector = () => {
    // フィードが1つしかない場合でも表示を強制するか検討
    if (feedOptions.length === 0) return null;

    return (
      <View style={styles.feedSelectorContainer}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.feedScrollContent}
          decelerationRate="fast"
          snapToInterval={100}
          snapToAlignment="start"
        >
          {feedOptions.map((feed) => (
            <TouchableOpacity
              key={feed.id}
              style={[
                styles.feedOption,
                selectedFeedId === feed.id && styles.feedOptionActive,
              ]}
              onPress={() => handleFeedSelect(feed.id)}
            >
              <Text
                style={[
                  styles.feedOptionText,
                  selectedFeedId === feed.id && styles.feedOptionTextActive,
                ]}
              >
                {feed.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderFooter = () => {
    if (!loading) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color="#fff" />
      </View>
    );
  };

  const renderEmpty = () => {
    if (loading) return null;
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>動画が見つかりませんでした</Text>
      </View>
    );
  };

  const isVerticalVideo = (video: VideoPost): boolean => {
    if (!video.aspectRatio) return false;
    const ratio = video.aspectRatio.width / video.aspectRatio.height;
    return ratio < 1; // 縦長の場合
  };

  const handleVideoPress = (item: VideoPost) => {
    if (isVerticalVideo(item)) {
      // 縦型動画の場合はShortsプレイヤーへ
      const verticalVideos = videos.filter(isVerticalVideo);
      const verticalIndex = verticalVideos.findIndex(v => v.id === item.id);
      navigation.navigate('ShortsPlayer', { 
        videos: verticalVideos, 
        initialIndex: verticalIndex >= 0 ? verticalIndex : 0 
      });
    } else {
      // 横型動画は通常の再生画面へ
      navigation.navigate('VideoPlayback', { videoId: item.id, video: item });
    }
  };

  const renderHeader = () => (
    <View style={[styles.headerContainer, { paddingTop: insets.top > 0 ? insets.top : 12 }]}>
      <View style={styles.headerLeft}>
        <View style={styles.logoContainer}>
          <Ionicons name="play-circle" size={28} color="#FF0000" />
          <Text style={styles.logoText}>ClipSky</Text>
        </View>
      </View>
      <View style={styles.headerRight}>
        <TouchableOpacity 
          style={styles.headerIconButton}
          onPress={() => navigation.navigate('Notifications')}
        >
          <Ionicons name="notifications-outline" size={24} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity 
          style={styles.headerIconButton}
          onPress={() => navigation.navigate('Search')}
        >
          <Ionicons name="search-outline" size={24} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {renderHeader()}
      {renderFeedSelector()}
      <FlatList
        data={videos}
        renderItem={({ item }) => (
          <VideoCard
            thumbnailUrl={item.thumbnailUrl}
            title={item.title}
            channelName={item.channelName}
            channelAvatar={item.channelAvatar}
            likeCount={item.likeCount}
            createdAt={item.createdAt}
            duration={item.duration}
            videoUrl={item.videoUrl}
            videoType={item.type}
            onPress={() => handleVideoPress(item)}
          />
        )}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        ListEmptyComponent={renderEmpty}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 8,
    backgroundColor: '#000',
  },
  headerLeft: {
    flex: 1,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoText: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 'bold',
    marginLeft: 6,
    letterSpacing: -0.5,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIconButton: {
    padding: 8,
    marginLeft: 4,
  },
  feedSelectorContainer: {
    backgroundColor: '#000',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
    zIndex: 100,
    minHeight: 50, // 最小高さを確保
  },
  feedScrollContent: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  feedOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginHorizontal: 4,
    borderRadius: 20,
    backgroundColor: '#1a1a1a',
  },
  feedOptionActive: {
    backgroundColor: '#fff',
  },
  feedOptionText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  feedOptionTextActive: {
    color: '#000',
    fontWeight: '600',
  },
  listContent: {
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  footer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  emptyText: {
    color: '#aaa',
    fontSize: 16,
  },
});

export default VideoTopScreen;
